import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle2, ShoppingCart, Send, CreditCard, Settings, Gamepad2, Users, Zap, Play, Star } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../ui/accordion";
import { Dialog, DialogContent, DialogTitle } from "../ui/dialog";
import { SectionHead, ServiceIcon } from "./Brand";
import { useContent, useSite } from "../../context/SiteContext";
import { fileUrl } from "../../lib/api";

function Bg({ desktop, mobile, overlay = "bg-[#03060f]/70" }) {
  return (
    <div className="absolute inset-0 -z-10">
      <picture>
        {mobile && <source media="(max-width: 767px)" srcSet={fileUrl(mobile)} />}
        <img src={fileUrl(desktop)} alt="" className="w-full h-full object-cover" />
      </picture>
      <div className={`absolute inset-0 ${overlay}`} />
    </div>
  );
}

export function Hero() {
  const h = useContent("hero");
  return (
    <section className="keep-dark relative isolate min-h-[100svh] md:min-h-[760px] flex overflow-hidden" data-testid="hero-section">
      <Bg desktop={h.bg_desktop} mobile={h.bg_mobile} overlay="bg-gradient-to-b md:bg-gradient-to-r from-[#02040b]/85 via-[#02040b]/45 to-transparent" />
      <div className="max-w-7xl w-full mx-auto px-5 lg:px-8 pt-28 pb-16 md:pt-0 md:pb-0 flex md:items-center">
        <div className="max-w-2xl fade-up">
          <p className="eyebrow !text-[0.78rem] sm:!text-sm" data-testid="hero-eyebrow">{h.eyebrow}</p>
          <h1 className="font-display mt-3 text-[3.4rem] leading-[0.86] sm:text-7xl lg:text-[6.6rem]" data-testid="hero-headline">
            <span className="block text-metal">{h.line1}</span>
            <span className="block text-electric">{h.line2}</span>
            <span className="block text-electric text-[2.6rem] sm:text-6xl lg:text-[5rem] -rotate-2 origin-left mt-1">{h.line3}</span>
          </h1>
          <span className="block h-1 w-48 mt-3 rounded-full bg-gradient-to-r from-[#00d9ff] to-transparent" />
          <p className="mt-5 text-base sm:text-lg text-slate-200 max-w-md" data-testid="hero-description">{h.description}</p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/quote" className="btn-electric" data-testid="hero-quote-btn">{h.button_primary} <ArrowRight className="w-4 h-4" /></Link>
            <a href="/#services" className="btn-ghost" data-testid="hero-services-btn">{h.button_secondary}</a>
          </div>
        </div>
      </div>
    </section>
  );
}

export function ServiceCard({ s, i, featured }) {
  return (
    <Link to={`/services/${s.slug}`} className={`card-neon card-neon-hover group relative p-6 flex gap-5 ${featured ? "border-[#00d9ff]/70 shadow-[0_0_30px_rgba(0,123,255,.3)]" : ""}`} data-testid={`service-card-${s.slug}`}>
      <div className="text-[#1aa3ff] shrink-0 pt-1 drop-shadow-[0_0_12px_rgba(0,140,255,.7)]"><ServiceIcon name={s.icon} className="w-12 h-12" /></div>
      <div className="flex-1 min-w-0 pr-10">
        <h3 className="font-display text-2xl sm:text-[1.7rem] text-white">{i + 1}. {s.title}</h3>
        {s.badge && <span className="inline-block mt-2 px-3 py-0.5 rounded bg-[#0a3a9a] text-white text-xs font-bold tracking-widest border border-[#2b7bff]">{s.badge}</span>}
        <p className="mt-2 text-slate-300">{s.description}</p>
        {s.bullets?.length > 0 && (
          <ul className="mt-3 space-y-1.5">
            {s.bullets.map((b) => (
              <li key={b} className="flex items-center gap-2 text-slate-200 text-[15px]"><CheckCircle2 className="w-4 h-4 text-[#00d9ff] shrink-0" />{b}</li>
            ))}
          </ul>
        )}
      </div>
      <span className="absolute right-5 bottom-5 icon-ring w-11 h-11 group-hover:translate-x-1 transition-transform"><ArrowRight className="w-5 h-5" /></span>
    </Link>
  );
}

export function ServicesGrid() {
  const c = useContent("services_section");
  const { site } = useSite();
  return (
    <section id="services" className="relative py-24 px-5 lg:px-8" data-testid="services-section">
      <SectionHead eyebrow={c.eyebrow} white={c.title_white} blue={c.title_blue} desc={c.description} />
      <div className="max-w-6xl mx-auto mt-14 grid gap-5 md:grid-cols-2">
        {(site?.services || []).map((s, i) => <ServiceCard key={s.id} s={s} i={i} featured={i === 0} />)}
      </div>
    </section>
  );
}

const STEP_ICONS = [ShoppingCart, Send, CreditCard, Settings, Gamepad2];

export function HowItWorks() {
  const c = useContent("how_it_works");
  return (
    <section className="relative py-24 px-5 lg:px-8" data-testid="how-it-works-section">
      <SectionHead eyebrow={c.eyebrow} white={c.title_white} blue={c.title_blue} desc={c.description} stacked />
      <ol className="max-w-3xl mx-auto mt-14 space-y-4">
        {(c.steps || []).map((s, i) => {
          const I = STEP_ICONS[i % 5];
          return (
            <li key={i} className="flex items-center gap-3 sm:gap-5 pb-4 border-b border-[#007bff]/20" data-testid={`how-step-${i + 1}`}>
              <span className="icon-ring w-10 h-10 sm:w-12 sm:h-12 font-cond font-bold text-xl text-white shrink-0">{i + 1}</span>
              <span className="hidden sm:block w-8 h-0.5 bg-[#007bff]" />
              <span className="icon-ring w-16 h-16 sm:w-20 sm:h-20 shrink-0"><I className="w-7 h-7 sm:w-9 sm:h-9" /></span>
              <div className="min-w-0">
                <h3 className="font-cond font-extrabold text-xl sm:text-2xl uppercase tracking-wide text-white">{s.title}</h3>
                <p className="text-slate-300 text-sm sm:text-base">{s.text}</p>
              </div>
            </li>
          );
        })}
      </ol>
    </section>
  );
}

const STAT_ICONS = [ShoppingCart, Users, Zap];

function CountUp({ text, run }) {
  const [t, setT] = useState(0);
  useEffect(() => {
    if (!run) return undefined;
    let raf;
    const start = performance.now();
    const tick = (now) => {
      const p = Math.min((now - start) / 1800, 1);
      setT(1 - Math.pow(1 - p, 3));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [run]);
  return <>{String(text || "").split(/(\d+)/).map((part, i) => (/^\d+$/.test(part) ? Math.round(Number(part) * t) : part))}</>;
}

export function Stats() {
  const c = useContent("stats");
  const ref = useRef(null);
  const [run, setRun] = useState(false);
  useEffect(() => {
    const io = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) {
        setRun(true);
        io.disconnect();
      }
    }, { threshold: 0.3 });
    if (ref.current) io.observe(ref.current);
    return () => io.disconnect();
  }, []);
  return (
    <section ref={ref} className="relative py-16 px-5 lg:px-8" data-testid="stats-section">
      <div className="max-w-5xl mx-auto card-neon stats-glow !rounded-3xl !border-[#007bff]/60 grid grid-cols-1 sm:grid-cols-3 divide-y sm:divide-y-0 sm:divide-x divide-[#00d9ff]/25">
        {(c.items || []).map((s, i) => {
          const I = STAT_ICONS[i % 3];
          return (
            <div key={i} className="py-8 px-4 flex flex-col items-center text-center" data-testid={`stat-${i}`}>
              <span className="icon-ring stat-pulse w-16 h-16" style={{ animationDelay: `${i * 0.4}s` }}><I className="w-8 h-8" /></span>
              <p className="mt-4 font-cond italic tracking-wider text-slate-300 uppercase text-lg">{s.label}</p>
              {s.value_top && <p className="font-display text-metal text-4xl">{s.value_top}</p>}
              <p className={`font-display text-electric stat-float ${s.value_top ? "text-5xl" : "text-6xl sm:text-7xl"}`} style={{ animationDelay: `${i * 0.5}s` }} data-testid={`stat-value-${i}`}><CountUp text={s.value} run={run} /></p>
            </div>
          );
        })}
      </div>
    </section>
  );
}

export function PageCanvas({ children }) {
  const c = useContent("page_background");
  return (
    <div className="relative" data-testid="home-canvas">
      <div className="sticky top-0 h-screen -mb-[100vh] overflow-hidden pointer-events-none" aria-hidden>
        <picture>
          {c.bg_mobile && <source media="(max-width: 767px)" srcSet={fileUrl(c.bg_mobile)} />}
          {c.bg_desktop && <img src={fileUrl(c.bg_desktop)} alt="" className="w-full h-full object-cover canvas-img" />}
        </picture>
        <div className="absolute inset-0 canvas-overlay" />
      </div>
      <div className="relative">{children}</div>
    </div>
  );
}

export function VideoPlayer({ v }) {
  const yt = v.video_url?.match(/(?:youtu\.be\/|v=|embed\/)([\w-]{11})/);
  if (yt) return <iframe title={v.title} className="w-full aspect-video rounded-lg" src={`https://www.youtube.com/embed/${yt[1]}`} allowFullScreen />;
  return <video className="w-full aspect-video rounded-lg bg-black" src={fileUrl(v.video_url)} poster={fileUrl(v.thumbnail)} controls autoPlay playsInline />;
}

export function VideosSection() {
  const c = useContent("videos_section");
  const { site } = useSite();
  const [active, setActive] = useState(null);
  const vids = site?.videos || [];
  return (
    <section className="relative py-24 px-5 lg:px-8" data-testid="videos-section">
      <div className="text-center">
        <span className="inline-block eyebrow px-6 py-1.5 rounded-full border-2 border-[#007bff]">{c.eyebrow}</span>
      </div>
      <div className="mt-4"><SectionHead white={c.title_white} blue={c.title_blue} desc={c.description} stacked /></div>
      <div className="max-w-6xl mx-auto mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
        {vids.map((v) => (
          <button key={v.id} onClick={() => setActive(v)} className="card-neon card-neon-hover text-left overflow-hidden group" data-testid={`video-card-${v.id}`}>
            <div className="relative aspect-video bg-[#050b1c]">
              {v.thumbnail && <img src={fileUrl(v.thumbnail)} alt={v.title} className="w-full h-full object-cover" />}
              <span className="absolute inset-0 grid place-items-center"><span className="icon-ring w-16 h-16 group-hover:scale-110 transition-transform"><Play className="w-7 h-7 ml-1" /></span></span>
            </div>
            <div className="p-4">
              <h3 className="font-cond font-bold text-xl uppercase text-white">{v.title}</h3>
              <p className="text-slate-400 text-sm line-clamp-2">{v.description}</p>
            </div>
          </button>
        ))}
      </div>
      {vids.length === 0 && <p className="text-center text-slate-400 mt-6" data-testid="videos-empty">New tutorial videos are coming soon.</p>}
      <Dialog open={!!active} onOpenChange={() => setActive(null)}>
        <DialogContent className="max-w-4xl bg-[#050a18] border-[#007bff]/40 p-3 sm:p-5">
          <DialogTitle className="font-cond uppercase text-white">{active?.title}</DialogTitle>
          {active && <VideoPlayer v={active} />}
          {active?.description && <p className="text-slate-300 text-sm">{active.description}</p>}
        </DialogContent>
      </Dialog>
    </section>
  );
}

export function ReviewCard({ r }) {
  return (
    <figure className="card-neon overflow-hidden flex flex-col" data-testid={`review-card-${r.id}`}>
      {r.image && <img src={fileUrl(r.image)} alt={`Review from ${r.name}`} className="w-full max-h-80 object-contain bg-black/40" />}
      <div className="p-5 flex-1 flex flex-col">
        {r.rating > 0 && <div className="flex gap-0.5 text-[#00d9ff]">{Array.from({ length: Number(r.rating) }).map((_, i) => <Star key={i} className="w-4 h-4 fill-current" />)}</div>}
        {r.text && <blockquote className="mt-2 text-slate-200 flex-1">“{r.text}”</blockquote>}
        <figcaption className="mt-3 font-cond font-bold uppercase tracking-wide text-white">{r.name}{r.service && <span className="text-[#00d9ff] font-semibold"> · {r.service}</span>}</figcaption>
      </div>
    </figure>
  );
}

export function ReviewsSection({ limit = 6, showAll = true }) {
  const c = useContent("reviews_section");
  const { site } = useSite();
  const reviews = (site?.reviews || []).slice(0, limit);
  return (
    <section className="py-24 px-5 lg:px-8" data-testid="reviews-section">
      <SectionHead eyebrow={c.eyebrow} white={c.title_white} blue={c.title_blue} desc={c.description} stacked />
      <div className="max-w-6xl mx-auto mt-12 columns-1 sm:columns-2 lg:columns-3 gap-5 [&>*]:mb-5 [&>*]:break-inside-avoid">
        {reviews.map((r) => <ReviewCard key={r.id} r={r} />)}
      </div>
      {reviews.length === 0 && <p className="text-center text-slate-400" data-testid="reviews-empty">Reviews from our customers will appear here soon.</p>}
      {showAll && reviews.length > 0 && <div className="text-center mt-4"><Link to="/reviews" className="btn-ghost" data-testid="all-reviews-btn">See all reviews</Link></div>}
    </section>
  );
}

export function FAQSection() {
  const c = useContent("faq_section");
  const { site } = useSite();
  return (
    <section className="py-24 px-5 lg:px-8" data-testid="faq-section">
      <SectionHead eyebrow={c.eyebrow} white={c.title_white} blue={c.title_blue} desc={c.description} />
      <Accordion type="single" collapsible className="max-w-3xl mx-auto mt-12 space-y-3">
        {(site?.faqs || []).map((f) => (
          <AccordionItem key={f.id} value={f.id} className="card-neon !border-b px-5" data-testid={`faq-item-${f.id}`}>
            <AccordionTrigger className="font-cond font-bold text-lg uppercase tracking-wide text-white hover:no-underline text-left">{f.question}</AccordionTrigger>
            <AccordionContent className="text-slate-300 text-base">{f.answer}</AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </section>
  );
}
